import { UseFormReturn } from 'react-hook-form';

import { DetailsForm } from '../schema';
import { FormStep, FormStepHeader } from './form/form-step';
import { DisputeStatus } from '../../../../../../../backend/src/types/types';
import { GenerateDetailsButton } from '@/new/components/generate-details-button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Textarea } from '@/new/components/shadcn/textarea';

export function DetailsInput({
  form,
  isGeneratingDetails,
  disabled,
}: {
  form: UseFormReturn<DetailsForm>;
  isGeneratingDetails?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      <div className="md:col-span-2">
        <FormField
          control={form.control}
          name="issues"
          render={({ field }) => (
            <FormItem>
              <FormMessage />

              <FormLabel>
                Matter Issues <span className="ml-1 text-teal-500">*</span>
              </FormLabel>
              <FormControl>
                <div className="relative">
                  {isGeneratingDetails && (
                    <>
                      <Skeleton className="absolute top-3 left-3 z-10 h-4 w-3/5 bg-gray-100" />
                      <Skeleton className="absolute top-8 left-3 z-10 h-4 w-4/5 bg-gray-100" />
                    </>
                  )}
                  <Textarea
                    {...field}
                    data-testid="matter-creator-description-field"
                    placeholder={isGeneratingDetails ? '' : 'Enter a list of high level matter issues...'}
                    className={isGeneratingDetails ? 'text-transparent' : ''}
                    rows={3}
                    disabled={disabled}
                  />
                </div>
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <div>
        <FormField
          control={form.control}
          name="disputeType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Matter Type (Optional)</FormLabel>
              <FormControl>
                <div className="relative">
                  {isGeneratingDetails && <Skeleton className="absolute inset-3 z-10 h-4 w-3/5 bg-gray-100" />}
                  <Input
                    {...field}
                    data-testid="matter-creator-type-field"
                    placeholder={
                      isGeneratingDetails ? '' : 'Arbitration, Employment Dispute, Commercial Litigation, etc...'
                    }
                    className={isGeneratingDetails ? 'text-transparent' : ''}
                    disabled={disabled}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div>
        <FormField
          control={form.control}
          name="userDefinedEntities"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Matter Entities (Optional)</FormLabel>
              <FormControl>
                <div className="relative">
                  {isGeneratingDetails && <Skeleton className="absolute inset-3 z-10 h-4 w-2/5 bg-gray-100" />}
                  <Input
                    {...field}
                    data-testid="matter-creator-entities-field"
                    placeholder={isGeneratingDetails ? '' : 'Key parties or entities in the matter...'}
                    className={isGeneratingDetails ? 'text-transparent' : ''}
                    disabled={disabled}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="md:col-span-2">
        <FormField
          control={form.control}
          name="additionalContext"
          render={({ field }) => (
            <FormItem>
              <FormMessage />

              <FormLabel>Additional Context (Optional)</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  data-testid="matter-creator-context-field"
                  placeholder="Any additional contextual information about the matter..."
                  rows={3}
                  disabled={disabled}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <div>
        <FormField
          control={form.control}
          name="timePeriod"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Time Period (Optional)</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  data-testid="matter-creator-time-period-field"
                  placeholder='e.g. "1960 to 1990" or "Early 2000s"...'
                  disabled={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div>
        <FormField
          control={form.control}
          name="disputeStatus"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Dispute Status (Optional)</FormLabel>
              <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                <FormControl>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select a dispute status..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(DisputeStatus).map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

interface DetailsStepProps {
  form: UseFormReturn<DetailsForm>;
  isGeneratingDetails?: boolean;
  onGenerateDetails?: (file: File) => void;
}

export function DetailsStep({ form, isGeneratingDetails, onGenerateDetails }: DetailsStepProps) {
  return (
    <FormStep>
      <FormStepHeader
        title="Matter Details"
        description="This information will be used in the process of determining which facts are relevant to your matter"
        icon="lucide--file-text"
        action={
          onGenerateDetails ? (
            <GenerateDetailsButton
              isGeneratingDetails={isGeneratingDetails ?? false}
              onGenerateDetails={onGenerateDetails}
            />
          ) : undefined
        }
      />
      <DetailsInput form={form} isGeneratingDetails={isGeneratingDetails} />
    </FormStep>
  );
}
